import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import { CurrencyInput } from "./CurrencyInput";

type Intervention = {
  id: string;
  intervention_type: string;
  title: string;
  provider: string | null;
  occurred_on: string;
  mileage_km: number | null;
  description: string | null;
  result: string | null;
  next_due_on: string | null;
  next_due_km: number | null;
  financial_entry_id: string | null;
};
type CheckItem = {
  label: string;
  status: "pending" | "ok" | "attention";
  notes: string;
};
type Checkup = {
  id: string;
  checkup_type: string;
  performed_on: string;
  mileage_km: number | null;
  status: string;
  items: CheckItem[];
  notes: string | null;
};
type Observation = {
  id: string;
  visibility: string;
  observation: string;
  created_at: string;
};
const defaults = [
  "Documentação",
  "Exterior e pintura",
  "Pneus e jantes",
  "Travões",
  "Iluminação",
  "Habitáculo",
  "Motor e fluidos",
  "Teste de estrada",
].map((label) => ({ label, status: "pending" as const, notes: "" }));
const interventionLabels: Record<string, string> = {
  maintenance: "Manutenção",
  repair: "Reparação",
  inspection: "Inspeção",
  preparation: "Preparação",
  cleaning: "Limpeza",
  tyres: "Pneus",
  bodywork: "Chapa e pintura",
  warranty: "Garantia",
  other: "Outra",
};
const checkupLabels: Record<string, string> = {
  entry: "Entrada",
  preparation: "Preparação",
  delivery: "Entrega",
  periodic: "Periódico",
};

export function VehicleChronologyPanel({
  vehicleId,
  organizationId,
  writable,
  canManageFinance,
  currencyCode,
}: {
  vehicleId: string;
  organizationId: string;
  writable: boolean;
  canManageFinance: boolean;
  currencyCode: string;
}) {
  const [tab, setTab] = useState<"interventions" | "checkups" | "observations">(
      "interventions",
    ),
    [interventions, setInterventions] = useState<Intervention[]>([]),
    [checkups, setCheckups] = useState<Checkup[]>([]),
    [observations, setObservations] = useState<Observation[]>([]),
    [message, setMessage] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [editingIntervention, setEditingIntervention] =
      useState<Intervention | null>(null),
    [editingCheckup, setEditingCheckup] = useState<Checkup | null>(null),
    [editingObservation, setEditingObservation] = useState<Observation | null>(
      null,
    ),
    [items, setItems] = useState<CheckItem[]>(defaults);
  const load = async () => {
    const [a, b, c] = await Promise.all([
      supabase
        .from("vehicle_interventions")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("vehicle_id", vehicleId)
        .is("archived_at", null)
        .order("occurred_on", { ascending: false }),
      supabase
        .from("vehicle_checkups")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("vehicle_id", vehicleId)
        .order("performed_on", { ascending: false }),
      supabase
        .from("vehicle_observations")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("vehicle_id", vehicleId)
        .is("archived_at", null)
        .order("created_at", { ascending: false }),
    ]);
    const error = a.error || b.error || c.error;
    if (error) setMessage(error.message);
    setInterventions((a.data ?? []) as Intervention[]);
    setCheckups((b.data ?? []) as Checkup[]);
    setObservations((c.data ?? []) as Observation[]);
  };
  useEffect(() => {
    void load();
  }, [vehicleId, organizationId]);
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message]);
  const saveIntervention = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("save_vehicle_intervention", {
      p_id: editingIntervention?.id ?? null,
      p_vehicle_id: vehicleId,
      p_type: String(f.get("type")),
      p_title: String(f.get("title")),
      p_provider: String(f.get("provider") || ""),
      p_occurred_on: String(f.get("date")),
      p_mileage_km: f.get("mileage") ? Number(f.get("mileage")) : null,
      p_description: String(f.get("description") || ""),
      p_result: String(f.get("result") || ""),
      p_next_due_on: f.get("next_date") ? String(f.get("next_date")) : null,
      p_next_due_km: f.get("next_km") ? Number(f.get("next_km")) : null,
      p_cost_cents:
        !editingIntervention && f.get("cost")
          ? Math.round(Number(f.get("cost")) * 100)
          : null,
      p_cost_state: String(f.get("cost_state") || "pending"),
      p_reason: editingIntervention ? String(f.get("reason") || "") : null,
    });
    setBusy(false);
    if (error) return setMessage(error.message);
    setEditingIntervention(null);
    event.currentTarget.reset();
    setMessage("Intervenção guardada.");
    await load();
  };
  const saveCheckup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("save_vehicle_checkup", {
      p_id: editingCheckup?.id ?? null,
      p_vehicle_id: vehicleId,
      p_type: String(f.get("type")),
      p_performed_on: String(f.get("date")),
      p_mileage_km: f.get("mileage") ? Number(f.get("mileage")) : null,
      p_status: String(f.get("status")),
      p_items: items,
      p_notes: String(f.get("notes") || ""),
    });
    setBusy(false);
    if (error) return setMessage(error.message);
    setEditingCheckup(null);
    setItems(defaults);
    event.currentTarget.reset();
    setMessage("Check-up guardado.");
    await load();
  };
  const saveObservation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    const f = new FormData(event.currentTarget);
    const { error } = await supabase.rpc("save_vehicle_observation", {
      p_id: editingObservation?.id ?? null,
      p_vehicle_id: vehicleId,
      p_visibility: String(f.get("visibility")),
      p_observation: String(f.get("observation")),
      p_reason: editingObservation ? String(f.get("reason") || "") : null,
    });
    setBusy(false);
    if (error) return setMessage(error.message);
    setEditingObservation(null);
    event.currentTarget.reset();
    setMessage("Observação guardada.");
    await load();
  };
  const archive = async (kind: "intervention" | "observation", id: string) => {
    const reason = window.prompt("Indique o motivo (mínimo 5 caracteres):");
    if (!reason) return;
    const { error } = await supabase.rpc(
      kind === "intervention"
        ? "archive_vehicle_intervention"
        : "archive_vehicle_observation",
      { p_id: id, p_reason: reason },
    );
    if (error) return setMessage(error.message);
    setMessage("Registo arquivado.");
    await load();
  };
  return (
    <section className="vehicle-chronology">
      <header>
        <div>
          <p className="stock-kicker">HISTÓRICO DA VIATURA</p>
          <h3>Cronologia operacional</h3>
          <p>
            Manutenções, verificações e notas reunidas no perfil da viatura.
          </p>
        </div>
      </header>
      <div className="chronology-guide" aria-label="Como funciona a cronologia">
        <article>
          <strong>Intervenções</strong>
          <span>
            Regista oficina, manutenção, reparações, custos e próximos prazos.
          </span>
        </article>
        <article>
          <strong>Check-up</strong>
          <span>
            Fotografa o estado da viatura na entrada, preparação, entrega ou
            noutro momento.
          </span>
        </article>
        <article>
          <strong>Observações</strong>
          <span>
            Conserva notas internas, comerciais ou públicas sem alterar os dados
            principais.
          </span>
        </article>
      </div>
      <nav className="chronology-tabs" aria-label="Áreas da cronologia">
        <button
          type="button"
          className={tab === "interventions" ? "active" : ""}
          onClick={() => setTab("interventions")}
        >
          Intervenções
        </button>
        <button
          type="button"
          className={tab === "checkups" ? "active" : ""}
          onClick={() => setTab("checkups")}
        >
          Check-up
        </button>
        <button
          type="button"
          className={tab === "observations" ? "active" : ""}
          onClick={() => setTab("observations")}
        >
          Observações
        </button>
      </nav>
      {message && <p className="stock-message">{message}</p>}
      {tab === "interventions" && (
        <div className="chronology-layout">
          <div className="chronology-list">
            {interventions.length === 0 ? (
              <p className="chronology-empty">
                Ainda não existem intervenções.
              </p>
            ) : (
              interventions.map((x) => (
                <article key={x.id}>
                  <div>
                    <strong>{x.title}</strong>
                    <span>
                      {interventionLabels[x.intervention_type]} ·{" "}
                      {new Date(x.occurred_on + "T12:00:00").toLocaleDateString(
                        "pt-PT",
                      )}
                      {x.provider ? ` · ${x.provider}` : ""}
                    </span>
                    {x.financial_entry_id && (
                      <small>Custo associado ao Financeiro</small>
                    )}
                  </div>
                  {writable && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setEditingIntervention(x)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => void archive("intervention", x.id)}
                      >
                        Arquivar
                      </button>
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
          {writable && (
            <form
              className="chronology-form"
              onSubmit={saveIntervention}
              key={editingIntervention?.id ?? "new-i"}
            >
              <h4>
                {editingIntervention
                  ? "Editar intervenção"
                  : "Nova intervenção"}
              </h4>
              <label>
                <span>Tipo</span>
                <select
                  name="type"
                  defaultValue={
                    editingIntervention?.intervention_type ?? "maintenance"
                  }
                >
                  {Object.entries(interventionLabels).map(([v, l]) => (
                    <option value={v} key={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Título *</span>
                <input
                  name="title"
                  required
                  defaultValue={editingIntervention?.title}
                />
              </label>
              <label>
                <span>Data *</span>
                <input
                  name="date"
                  type="date"
                  required
                  defaultValue={
                    editingIntervention?.occurred_on ??
                    new Date().toISOString().slice(0, 10)
                  }
                />
              </label>
              <label>
                <span>Quilometragem</span>
                <input
                  name="mileage"
                  type="number"
                  min="0"
                  defaultValue={editingIntervention?.mileage_km ?? ""}
                />
              </label>
              <label>
                <span>Prestador</span>
                <input
                  name="provider"
                  defaultValue={editingIntervention?.provider ?? ""}
                />
              </label>
              <label>
                <span>Descrição</span>
                <textarea
                  name="description"
                  defaultValue={editingIntervention?.description ?? ""}
                />
              </label>
              <label>
                <span>Resultado</span>
                <textarea
                  name="result"
                  defaultValue={editingIntervention?.result ?? ""}
                />
              </label>
              <div className="chronology-pair">
                <label>
                  <span>Próxima data</span>
                  <input
                    name="next_date"
                    type="date"
                    defaultValue={editingIntervention?.next_due_on ?? ""}
                  />
                </label>
                <label>
                  <span>Próximos km</span>
                  <input
                    name="next_km"
                    type="number"
                    min="0"
                    defaultValue={editingIntervention?.next_due_km ?? ""}
                  />
                </label>
              </div>
              {canManageFinance && !editingIntervention && (
                <div className="chronology-pair">
                  <label>
                    <span>Custo ({currencyCode})</span>
                    <CurrencyInput currencyCode={currencyCode} name="cost" type="number" min="0" step="0.01" />
                  </label>
                  <label>
                    <span>Estado do custo</span>
                    <select name="cost_state">
                      <option value="pending">Pendente</option>
                      <option value="invoiced">Faturado</option>
                      <option value="paid">Pago</option>
                    </select>
                  </label>
                </div>
              )}
              {editingIntervention && (
                <label>
                  <span>Motivo da alteração *</span>
                  <input name="reason" required minLength={5} />
                </label>
              )}
              <footer>
                {editingIntervention && (
                  <button
                    type="button"
                    onClick={() => setEditingIntervention(null)}
                  >
                    Cancelar
                  </button>
                )}
                <button className="stock-primary" disabled={busy}>
                  Guardar
                </button>
              </footer>
            </form>
          )}
        </div>
      )}
      {tab === "checkups" && (
        <div className="chronology-layout">
          <div className="chronology-list">
            {checkups.length === 0 ? (
              <p className="chronology-empty">Ainda não existem check-ups.</p>
            ) : (
              checkups.map((x) => (
                <article key={x.id}>
                  <div>
                    <strong>{checkupLabels[x.checkup_type]}</strong>
                    <span>
                      {new Date(
                        x.performed_on + "T12:00:00",
                      ).toLocaleDateString("pt-PT")}{" "}
                      · {x.status === "completed" ? "Concluído" : "Rascunho"}
                    </span>
                    <small>
                      {x.items.filter((i) => i.status === "attention").length}{" "}
                      ponto(s) com atenção
                    </small>
                  </div>
                  {writable && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingCheckup(x);
                        setItems(x.items);
                      }}
                    >
                      Editar
                    </button>
                  )}
                </article>
              ))
            )}
          </div>
          {writable && (
            <form
              className="chronology-form"
              onSubmit={saveCheckup}
              key={editingCheckup?.id ?? "new-c"}
            >
              <h4>{editingCheckup ? "Editar check-up" : "Novo check-up"}</h4>
              <div className="chronology-pair">
                <label>
                  <span>Momento</span>
                  <select
                    name="type"
                    defaultValue={editingCheckup?.checkup_type ?? "entry"}
                  >
                    {Object.entries(checkupLabels).map(([v, l]) => (
                      <option value={v} key={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Data *</span>
                  <input
                    name="date"
                    type="date"
                    required
                    defaultValue={
                      editingCheckup?.performed_on ??
                      new Date().toISOString().slice(0, 10)
                    }
                  />
                </label>
              </div>
              <label>
                <span>Quilometragem</span>
                <input
                  name="mileage"
                  type="number"
                  min="0"
                  defaultValue={editingCheckup?.mileage_km ?? ""}
                />
              </label>
              <div className="checkup-items">
                {items.map((item, index) => (
                  <div key={item.label}>
                    <strong>{item.label}</strong>
                    <select
                      aria-label={`Estado: ${item.label}`}
                      value={item.status}
                      onChange={(e) =>
                        setItems((current) =>
                          current.map((x, i) =>
                            i === index
                              ? {
                                  ...x,
                                  status: e.target.value as CheckItem["status"],
                                }
                              : x,
                          ),
                        )
                      }
                    >
                      <option value="pending">Por verificar</option>
                      <option value="ok">Conforme</option>
                      <option value="attention">Requer atenção</option>
                    </select>
                    <input
                      aria-label={`Nota: ${item.label}`}
                      placeholder="Nota opcional"
                      value={item.notes}
                      onChange={(e) =>
                        setItems((current) =>
                          current.map((x, i) =>
                            i === index ? { ...x, notes: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  </div>
                ))}
              </div>
              <label>
                <span>Estado</span>
                <select
                  name="status"
                  defaultValue={editingCheckup?.status ?? "draft"}
                >
                  <option value="draft">Rascunho</option>
                  <option value="completed">Concluído</option>
                </select>
              </label>
              <label>
                <span>Notas gerais</span>
                <textarea
                  name="notes"
                  defaultValue={editingCheckup?.notes ?? ""}
                />
              </label>
              <footer>
                {editingCheckup && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCheckup(null);
                      setItems(defaults);
                    }}
                  >
                    Cancelar
                  </button>
                )}
                <button className="stock-primary" disabled={busy}>
                  Guardar
                </button>
              </footer>
            </form>
          )}
        </div>
      )}
      {tab === "observations" && (
        <div className="chronology-layout">
          <div className="chronology-list">
            {observations.length === 0 ? (
              <p className="chronology-empty">Ainda não existem observações.</p>
            ) : (
              observations.map((x) => (
                <article key={x.id}>
                  <div>
                    <strong>
                      {x.visibility === "internal"
                        ? "Interna"
                        : x.visibility === "commercial"
                          ? "Comercial"
                          : "Pública"}
                    </strong>
                    <span>{x.observation}</span>
                    <small>
                      {new Date(x.created_at).toLocaleString("pt-PT")}
                    </small>
                  </div>
                  {writable && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setEditingObservation(x)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => void archive("observation", x.id)}
                      >
                        Arquivar
                      </button>
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
          {writable && (
            <form
              className="chronology-form"
              onSubmit={saveObservation}
              key={editingObservation?.id ?? "new-o"}
            >
              <h4>
                {editingObservation ? "Editar observação" : "Nova observação"}
              </h4>
              <label>
                <span>Visibilidade</span>
                <select
                  name="visibility"
                  defaultValue={editingObservation?.visibility ?? "internal"}
                >
                  <option value="internal">Interna</option>
                  <option value="commercial">Comercial</option>
                  <option value="public">Pública</option>
                </select>
              </label>
              <label>
                <span>Observação *</span>
                <textarea
                  name="observation"
                  required
                  minLength={2}
                  defaultValue={editingObservation?.observation ?? ""}
                />
              </label>
              {editingObservation && (
                <label>
                  <span>Motivo da alteração *</span>
                  <input name="reason" required minLength={5} />
                </label>
              )}
              <footer>
                {editingObservation && (
                  <button
                    type="button"
                    onClick={() => setEditingObservation(null)}
                  >
                    Cancelar
                  </button>
                )}
                <button className="stock-primary" disabled={busy}>
                  Guardar
                </button>
              </footer>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
